import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  styled,
  Pagination,
  Button,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  NewReleases as NewIcon,
  Bookmark as BookmarkIcon,
  FilterList as FilterIcon,
  LibraryBooks as LibraryIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MagazineCard from "../../components/cards/MagazineCard";
import useMagazineList from "../../hooks/api-request/useMagazineList";

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const PageHeader = styled(Box)(({ theme }) => ({
  position: "relative",
  marginBottom: theme.spacing(6),
  textAlign: "center",
  padding: theme.spacing(4, 2),
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  borderRadius: theme.shape.borderRadius * 2,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(1),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.paper,
    transition: "all 0.2s ease",
    "&:hover, &.Mui-focused": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

const FilterChip = styled(Chip)(({ theme, selected }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  fontWeight: 600,
  backgroundColor: selected
    ? theme.palette.primary.main
    : alpha(theme.palette.primary.main, 0.04),
  color: selected ? theme.palette.common.white : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.08),
  },
}));

const SortSelect = styled(TextField)(({ theme }) => ({
  minWidth: 200,
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 2,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(8, 2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  marginTop: theme.spacing(4),
}));

// Custom styled pagination for better RTL support
const StyledPagination = styled(Pagination, {
  shouldForwardProp: (prop) => prop !== "isRtl"
})(({ theme, isRtl }) => ({
  "& .MuiPaginationItem-root": {
    margin: isRtl ? "0 0 0 4px" : "0 4px 0 0",
  },
  "& .MuiPaginationItem-icon": {
    transform: isRtl ? "rotate(180deg)" : "rotate(0deg)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiPaginationItem-root": {
      minWidth: 28,
      height: 28,
      fontSize: "0.75rem",
    }
  }
}));

const Magazine = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState({
    frequency: "",
    search: "",
  });
  const [sort, setSort] = useState("-publication_date");
  const [page, setPage] = useState(1);
  const [page_size, setPageSize] = useState(12); // Default page size for magazines

  const { data, isLoading } = useMagazineList(filters, sort, page, page_size);
  const magazines = data?.results || [];
  const totalPages = data?.total_pages || 1;

  const handleSearch = (event) => {
    setFilters((prev) => ({ ...prev, search: event.target.value }));
    setPage(1);
  };

  const handleFrequencyFilter = (frequency) => {
    setFilters((prev) => ({
      ...prev,
      frequency: prev.frequency === frequency ? "" : frequency,
    }));
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const FREQUENCY_OPTIONS = [
    { value: "WEEKLY", label: t('magazine.frequency.weekly'), icon: <TrendingIcon /> },
    { value: "MONTHLY", label: t('magazine.frequency.monthly'), icon: <NewIcon /> },
    { value: "QUARTERLY", label: t('magazine.frequency.quarterly'), icon: <BookmarkIcon /> },
  ];
  
  const SORT_OPTIONS = [
    { value: "-publication_date", label: t('magazine.sort.latestFirst') },
    { value: "publication_date", label: t('magazine.sort.oldestFirst') },
    { value: "title", label: t('magazine.sort.titleAZ') },
    { value: "-title", label: t('magazine.sort.titleZA') },
  ];

  return (
    <PageWrapper>
      <Container maxWidth="xl">
        <PageHeader>
          <HeaderTitle variant="h2">{t('magazine.title')}</HeaderTitle>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            {t('magazine.subtitle')}
          </Typography>
        </PageHeader>

        <SearchContainer>
          <SearchField
            fullWidth
            placeholder={t('magazine.searchPlaceholder')}
            value={filters.search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {FREQUENCY_OPTIONS.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                icon={option.icon}
                onClick={() => handleFrequencyFilter(option.value)}
                selected={filters.frequency === option.value}
              />
            ))}
          </Box>

          <SortSelect
            select
            value={sort}
            onChange={handleSortChange}
            SelectProps={{
              IconComponent: FilterIcon,
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </SortSelect>
        </SearchContainer>

        {isLoading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid item xs={6} md={3} key={index}>
                <MagazineCard loading />
              </Grid>
            ))}
          </Grid>
        ) : magazines.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {magazines.map((magazine) => (
                <Grid item xs={6} md={3} key={magazine.id}>
                  <MagazineCard magazine={magazine} />
                </Grid>
              ))}
            </Grid>

            <Stack
              direction="row"
              justifyContent="center"
              sx={{
                mt: 6,
                width: "100%",
                overflow: "hidden"
              }}
            >
              <StyledPagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                isRtl={isRtl}
                boundaryCount={isMobile ? 1 : 2}
                siblingCount={isMobile ? 0 : 1}
                shape="rounded"
                dir={isRtl ? "rtl" : "ltr"}
              />
            </Stack>
          </>
        ) : (
          <EmptyState>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('magazine.noMagazinesFound')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('magazine.tryAdjustingFilters')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({ frequency: "", search: "" });
                setSort("-publication_date");
                setPage(1);
              }}
            >
              {t('magazine.clearAllFilters')}
            </Button>
          </EmptyState>
        )}
      </Container>
    </PageWrapper>
  );
};

export default Magazine;
